import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Form, Header, Icon, Input, Loader, Message, Modal, Segment, Table } from 'semantic-ui-react';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { saoPauloBoundToUtcIso } from '../../utils/saoPauloFilterBounds';

type EmailItem = {
  id: string;
  contractId: string;
  clienteNome: string;
  cnpj?: string;
  kind: string;
  trigger: string;
  toEmail?: string;
  subject: string;
  status: string;
  error?: string;
  attempts: number;
  createdAt: string;
  sentAt?: string;
  bodyHtml?: string;
  fromEmail?: string;
  smtpMessageId?: string;
};

const kindLabel: Record<string, string> = {
  BoasVindas: 'Boas-vindas',
  FinalizadoRenovacao: 'Finalizado — renovação',
  FinalizadoNovaLinha: 'Finalizado — linha nova',
};

const statusLabel: Record<string, string> = {
  Pendente: 'Pendente',
  Enviado: 'Enviado',
  Falhou: 'Falhou',
  Ignorado: 'Ignorado',
};

function formatWhen(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
}

const PosVendaEmailsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const podeReenviar = hasRole('Admin');
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState<EmailItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<EmailItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendError, setResendError] = useState('');
  const [resending, setResending] = useState(false);
  const resendLock = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getPosVendaEmails({
        status: status || undefined,
        kind: kind || undefined,
        from: saoPauloBoundToUtcIso(from, 'from'),
        to: saoPauloBoundToUtcIso(to, 'to'),
        page,
        pageSize: 50,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err?.response?.data || err?.message || 'Não foi possível carregar os e-mails.');
    } finally {
      setLoading(false);
    }
  }, [status, kind, from, to, page]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (item: EmailItem) => {
    setSelected(item);
    setResendEmail(item.toEmail || '');
    setResendError('');
    setDetailLoading(true);
    try {
      const detail = await apiService.getPosVendaEmail(item.id);
      setSelected(detail);
      setResendEmail(detail.toEmail || '');
    } catch (err: any) {
      setResendError(err?.response?.data || 'Não foi possível abrir o e-mail.');
    } finally {
      setDetailLoading(false);
    }
  };

  const resend = async () => {
    if (!selected || resendLock.current) return;
    resendLock.current = true;
    setResending(true);
    setResendError('');
    try {
      await apiService.resendPosVendaEmail(selected.id, resendEmail);
      setSelected(null);
      await load();
    } catch (err: any) {
      setResendError(err?.response?.data || 'Não foi possível reenviar.');
    } finally {
      resendLock.current = false;
      setResending(false);
    }
  };

  const canResend =
    podeReenviar && (selected?.status === 'Falhou' || selected?.status === 'Ignorado');
  const pageCount = Math.max(1, Math.ceil(total / 50));

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <Button as={Link} to="/pos-venda" basic>
          <Icon name="arrow left" /> Voltar
        </Button>
      </div>
      <Header as="h2">
        <Icon name="mail" />
        <Header.Content>
          E-mails do pós-venda
          <Header.Subheader>O que saiu automaticamente para o cliente</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Form>
          <Form.Group widths="equal">
            <Form.Field>
              <label>Status</label>
              <select value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}>
                <option value="">Todos</option>
                <option value="Enviado">Enviado</option>
                <option value="Pendente">Pendente</option>
                <option value="Falhou">Falhou</option>
                <option value="Ignorado">Ignorado</option>
              </select>
            </Form.Field>
            <Form.Field>
              <label>Tipo</label>
              <select value={kind} onChange={(event) => { setPage(1); setKind(event.target.value); }}>
                <option value="">Todos</option>
                <option value="BoasVindas">Boas-vindas</option>
                <option value="FinalizadoRenovacao">Finalizado — renovação</option>
                <option value="FinalizadoNovaLinha">Finalizado — linha nova</option>
              </select>
            </Form.Field>
            <Form.Field>
              <label>Criado de</label>
              <Input type="date" value={from} onChange={(_, data) => { setPage(1); setFrom(String(data.value)); }} />
            </Form.Field>
            <Form.Field>
              <label>Criado até</label>
              <Input type="date" value={to} onChange={(_, data) => { setPage(1); setTo(String(data.value)); }} />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      {error && <Message negative>{String(error)}</Message>}
      {loading && <Loader active inline="centered" />}

      {!loading && (
        <Table celled selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Quando</Table.HeaderCell>
              <Table.HeaderCell>Cliente</Table.HeaderCell>
              <Table.HeaderCell>Tipo</Table.HeaderCell>
              <Table.HeaderCell>Destino</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={5}>Nenhum e-mail neste filtro.</Table.Cell>
              </Table.Row>
            )}
            {items.map((item) => (
              <Table.Row key={item.id} style={{ cursor: 'pointer' }} onClick={() => openDetail(item)}>
                <Table.Cell>{formatWhen(item.sentAt || item.createdAt)}</Table.Cell>
                <Table.Cell>
                  {item.clienteNome || '—'}
                  <div style={{ color: '#667', fontSize: '0.85rem' }}>{item.cnpj}</div>
                </Table.Cell>
                <Table.Cell>{kindLabel[item.kind] || item.kind}</Table.Cell>
                <Table.Cell>{item.toEmail || '—'}</Table.Cell>
                <Table.Cell>{statusLabel[item.status] || item.status}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <Button disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Anterior</Button>
        <span>Página {page} de {pageCount}</span>
        <Button disabled={page >= pageCount} onClick={() => setPage((current) => current + 1)}>Próxima</Button>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} size="large">
        <Modal.Header>{selected?.subject}</Modal.Header>
        <Modal.Content scrolling>
          {detailLoading && <Loader active inline="centered" />}
          {selected && !detailLoading && (
            <>
              <p><strong>Cliente:</strong> {selected.clienteNome} {selected.cnpj ? `· ${selected.cnpj}` : ''}</p>
              <p><strong>Destino:</strong> {selected.toEmail || '—'} · <strong>Status:</strong> {statusLabel[selected.status] || selected.status}</p>
              {selected.error && <Message warning>{selected.error}</Message>}
              {selected.bodyHtml && (
                <iframe
                  title="Conteúdo enviado"
                  sandbox=""
                  srcDoc={selected.bodyHtml}
                  style={{ width: '100%', minHeight: '320px', border: '1px solid #ddd' }}
                />
              )}
              {canResend && (
                <Form style={{ marginTop: '1rem' }}>
                  <Form.Field>
                    <label>Endereço de destino</label>
                    <Input value={resendEmail} onChange={(_, data) => setResendEmail(String(data.value))} />
                  </Form.Field>
                  {resendError && <Message negative>{String(resendError)}</Message>}
                </Form>
              )}
            </>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setSelected(null)}>Fechar</Button>
          {canResend && (
            <Button primary loading={resending} disabled={resending} onClick={resend}>Reenviar</Button>
          )}
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default PosVendaEmailsPage;
